import { useMutation, useQueryClient } from '@tanstack/react-query';
import MessageTemplateService, {
  IUpdateMessageTemplateParams,
} from '@clickersphere/core/services/MessageTemplateService';
import { useBuildQueryKey } from '../useBuildQueryKey';
import {
  MessageTemplateQueryData,
  MESSAGE_TEMPLATES_QUERY_KEY,
} from './useMessageTemplates';

interface IUpdateMessageTemplateVariables {
  id: string;
  data: IUpdateMessageTemplateParams;
}

export function useUpdateMessageTemplate() {
  const queryClient = useQueryClient();
  const { buildQueryKey } = useBuildQueryKey();

  const {
    isPending: updateMessageTemplateIsPending,
    mutateAsync: updateMessageTemplateAsync,
  } = useMutation({
    mutationFn: async ({ id, data }: IUpdateMessageTemplateVariables) =>
      MessageTemplateService.update(id, data),
    onMutate: async ({ id, data }) => {
      await queryClient.cancelQueries({
        queryKey: buildQueryKey(MESSAGE_TEMPLATES_QUERY_KEY),
      });

      const oldTemplates = queryClient.getQueryData<MessageTemplateQueryData[]>(
        buildQueryKey(MESSAGE_TEMPLATES_QUERY_KEY),
      );

      queryClient.setQueryData<MessageTemplateQueryData[]>(
        buildQueryKey(MESSAGE_TEMPLATES_QUERY_KEY),
        (old) =>
          old?.map((template) =>
            template.id === id
              ? { ...template, ...data, queryStatus: 'pending' }
              : template,
          ),
      );

      return { oldTemplates };
    },
    onSuccess: async (_data, { id }) => {
      await queryClient.cancelQueries({
        queryKey: buildQueryKey(MESSAGE_TEMPLATES_QUERY_KEY),
      });

      queryClient.setQueryData<MessageTemplateQueryData[]>(
        buildQueryKey(MESSAGE_TEMPLATES_QUERY_KEY),
        (old) =>
          old?.map((template) =>
            template.id === id
              ? { ...template, queryStatus: undefined }
              : template,
          ),
      );
    },
    onError: async (_error, _variables, context) => {
      await queryClient.cancelQueries({
        queryKey: buildQueryKey(MESSAGE_TEMPLATES_QUERY_KEY),
      });

      queryClient.setQueryData(
        buildQueryKey(MESSAGE_TEMPLATES_QUERY_KEY),
        context?.oldTemplates,
      );
    },
  });

  return {
    updateMessageTemplateAsync,
    updateMessageTemplateIsPending,
  };
}
